import ChevronDown from '@atlaskit/icon/glyph/chevron-down';
import Popup from '@atlaskit/popup';
import { Box, Flex, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { defaultStoreUtils } from '@databases/redux';
import { Database } from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import { getNumberDisplayString } from '@databases/utils/database/field-types/number';
import {
  fieldHasCalculations,
  getAllEntriesInField,
  getDatabaseFieldRequired,
  getFieldCalculationType,
} from '@databases/utils/database/utils';
import { selectFieldNumberFormat } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { DatabaseCalculationLabel } from './DatabaseCalculationLabel';
import { QuickCalcOptions } from './QuickCalcOptions';
import { getCalculationFunction } from './calculations';
import {
  formatPercent,
  handleCalculationSelectionChange,
  isPercentCalculation,
} from './quickCalcUtils';
import { CalculationSelectOption } from './types';

interface QuickCalculationsSelectProps {
  fieldId: string;
  database: Database;
  width: number;
  canEditContent: boolean;
}

const QuickCalculationsSelect: FunctionComponent<QuickCalculationsSelectProps> = ({
  fieldId,
  database,
  width,
  canEditContent,
}) => {
  const { view } = useView();
  const dispatch = defaultStoreUtils.useDispatch();

  const [numberFormat, field, entries] = defaultStoreUtils.useEqualSelector((state) => {
    const numberFormat = selectFieldNumberFormat(state, { database, fieldId });
    const field = getDatabaseFieldRequired(database, fieldId);
    const entries = getAllEntriesInField(database, fieldId, state);

    return [numberFormat, field, entries] as const;
  });

  const handleChange = useCallback(
    (option: CalculationSelectOption | null) => {
      handleCalculationSelectionChange(
        option,
        database.contentId,
        dispatch,
        fieldId,
        view.id
      );
    },
    [database.contentId, dispatch, fieldId, view.id]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isButtonVisible, setIsButtonVisible] = useState(false);
  const { locale } = useIntl();
  const analyticsClient = useAnalytics();

  const pressableStyles = xcss({
    width: '100%',
    borderRadius: 'border.radius.100',
    paddingTop: 'space.075',
    paddingBottom: 'space.075',
    // if changing left or right padding, it must be updated in the resizing
    // logic to avoid ui bugs.
    paddingLeft: 'space.150',
    paddingRight: 'space.150',
    position: 'relative',
    maxHeight: '36px',
    visibility:
      fieldHasCalculations(fieldId, view.settings.calculations) || isButtonVisible
        ? 'visible'
        : 'hidden',
    ':before': {
      content: '""',
      position: 'absolute',
      zIndex: 'card',
      width: '100%',
      height: '8px',
      bottom: 'space.negative.100',
      left: 'space.0',
    },
    ':after': {
      content: '""',
      position: 'absolute',
      zIndex: 'card',
      width: '100%',
      height: '8px',
      top: 'space.negative.100',
      left: 'space.0',
    },
  });

  const buttonContainerStyles = xcss({
    width: '100%',
    paddingTop: 'space.075',
    paddingLeft: 'space.025',
    paddingRight: 'space.025',
  });

  const calculationLabelInlineStyles = xcss({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  });

  const popupStyles = xcss({ maxHeight: '300px' });
  const getCalculationResultsContainerStyles = (hideCalculationLabel: boolean) => {
    return xcss({
      overflowWrap: 'unset',
      fontWeight: 'bold',
      textOverflow: hideCalculationLabel ? 'ellipsis' : 'unset',
      overflow: 'hidden',
    });
  };

  const calculationType = useMemo(() => {
    return view.settings.calculations?.[fieldId];
  }, [fieldId, view.settings.calculations]);

  const handleOnClick = (databaseCalculationType?: DatabaseCalculationType) => {
    if (databaseCalculationType) {
      handleChange({ value: databaseCalculationType, label: databaseCalculationType });
      if (calculationType && calculationType !== databaseCalculationType) {
        analyticsClient.sendCalculationEventConverted({
          databaseContainer: getDatabaseContainerType(),
          fieldId: field.id,
          fromCalculationType: calculationType,
          toCalculationType: databaseCalculationType,
          source: 'quickCalculationSelect',
          contentId: database.contentId,
        });
      } else {
        analyticsClient.sendCreateCalculationEvent({
          databaseContainer: getDatabaseContainerType(),
          fieldId: field.id,
          calculationType: databaseCalculationType,
          source: 'quickCalculationSelect',
          contentId: database.contentId,
        });
      }
    } else {
      handleChange(null);
      analyticsClient.sendRemoveCalculationEvent({
        databaseContainer: getDatabaseContainerType(),
        fieldId: field.id,
        source: 'quickCalculationSelect',
        contentId: database.contentId,
      });
    }
    setIsOpen(false);
  };

  const calculationResult = useMemo(() => {
    if (!calculationType || !numberFormat) {
      return undefined;
    }

    let preformattedResult;
    if (calculationType === DatabaseCalculationType.COUNT_UNIQUE) {
      const calculationFunction = getCalculationFunction(
        DatabaseCalculationType.COUNT_UNIQUE
      );
      const store = defaultStoreUtils.useStore();
      preformattedResult = calculationFunction(database, fieldId, entries, store);
    } else {
      const calculationFunction = getCalculationFunction(calculationType);
      preformattedResult = calculationFunction(database, fieldId, entries);
    }

    // For some calculations, it doesn't make sense to apply the custom formatting.
    const formattedResult = getNumberDisplayString(
      preformattedResult.toString(),
      field,
      locale,
      [
        DatabaseCalculationType.COUNT,
        DatabaseCalculationType.COUNT_UNIQUE,
        DatabaseCalculationType.COUNT_EMPTY,
        DatabaseCalculationType.PERCENT_EMPTY,
        DatabaseCalculationType.PERCENT_NOT_EMPTY,
      ].includes(calculationType)
    );
    if (formattedResult === 'NaN') {
      return '-';
    } else {
      if (isPercentCalculation(calculationType)) {
        return formatPercent(calculationType, formattedResult);
      } else {
        return formattedResult;
      }
    }
  }, [calculationType, database, entries, field, fieldId, locale, numberFormat]);

  const calculationResultRef = useRef<HTMLSpanElement>(null);
  const calculationLabelRef = useRef<HTMLSpanElement>(null);
  const chevronRef = useRef<HTMLSpanElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const [hideCalculationLabel, setHideCalculationLabel] = useState(false);
  const [savedLabelWidth, setSavedLabelWidth] = useState<number>(0);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const buttonContainerWidth = buttonContainerRef.current?.offsetWidth || 0;
    const labelWidth = calculationLabelRef.current?.offsetWidth || 0;
    const chevronWidth = chevronRef.current?.offsetWidth || 0;
    const calculationResultWidth = calculationResultRef.current?.offsetWidth || 0;
    const padding = 14 + 14; // padding on each side of the button

    const contentWidth = labelWidth + chevronWidth + calculationResultWidth + padding + 2; // 2px added for buffer
    const gap = buttonContainerWidth - contentWidth;

    if (!showTooltip && gap <= 0) {
      setShowTooltip(true);
    } else if (showTooltip && gap > 0) {
      setShowTooltip(false);
    }

    if (!hideCalculationLabel) {
      setSavedLabelWidth(labelWidth);
    }

    if (hideCalculationLabel && gap - savedLabelWidth > 0) {
      setHideCalculationLabel(false);
    }

    if (!hideCalculationLabel && contentWidth >= buttonContainerWidth) {
      setHideCalculationLabel(true);
    }
  }, [
    calculationResult,
    field.name,
    fieldId,
    hideCalculationLabel,
    savedLabelWidth,
    showTooltip,
    width,
  ]);

  useEffect(() => {
    if (!fieldHasCalculations(fieldId, view.settings.calculations)) {
      setIsButtonVisible(false);
    }
  }, [fieldId, view.settings.calculations]);

  return (
    <Flex>
      {
        <Popup
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          placement="top-start"
          content={() => {
            return (
              <Box
                xcss={popupStyles}
                onMouseLeave={() => {
                  if (
                    canEditContent &&
                    !fieldHasCalculations(fieldId, view.settings.calculations)
                  ) {
                    setIsButtonVisible(false);
                    setIsOpen(false);
                  }
                }}
                onMouseEnter={() => {
                  if (
                    canEditContent &&
                    !fieldHasCalculations(fieldId, view.settings.calculations)
                  ) {
                    setIsButtonVisible(true);
                    setIsOpen(true);
                  }
                }}
              >
                <QuickCalcOptions
                  field={field}
                  handleOnClick={handleOnClick}
                  calculations={view.settings.calculations}
                />
              </Box>
            );
          }}
          trigger={(triggerProps) => {
            return (
              <Flex xcss={xcss({ width: '100%' })}>
                <Box
                  {...triggerProps}
                  ref={buttonContainerRef}
                  xcss={buttonContainerStyles}
                  onMouseEnter={() => {
                    if (!fieldHasCalculations(fieldId, view.settings.calculations)) {
                      setIsButtonVisible(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!fieldHasCalculations(fieldId, view.settings.calculations)) {
                      setIsButtonVisible(false);
                      setIsOpen(false);
                    }
                  }}
                >
                  <Tooltip content={showTooltip ? calculationResult : ''} position="top">
                    {(tooltipProps) => (
                      <Box {...tooltipProps}>
                        <Pressable
                          placeholder={undefined}
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                          {...triggerProps}
                          onClick={() => setIsOpen(!isOpen)}
                          xcss={pressableStyles}
                        >
                          <Inline spread="space-between" alignBlock="center">
                            <Inline
                              alignBlock="center"
                              xcss={calculationLabelInlineStyles}
                            >
                              {!hideCalculationLabel && (
                                <Text
                                  weight="semibold"
                                  color="color.text.subtlest"
                                  ref={calculationLabelRef}
                                  maxLines={1}
                                >
                                  <DatabaseCalculationLabel
                                    type={getFieldCalculationType(
                                      fieldId,
                                      view.settings.calculations
                                    )}
                                  />
                                </Text>
                              )}
                              <Inline ref={chevronRef}>
                                <ChevronDown
                                  label=""
                                  primaryColor={token('color.text.subtlest')}
                                />
                              </Inline>
                            </Inline>
                            <Inline
                              alignBlock="center"
                              xcss={xcss({ overflow: 'hidden', overflowWrap: 'unset' })}
                            >
                              <Box
                                as="span"
                                color="color.text"
                                ref={calculationResultRef}
                                xcss={getCalculationResultsContainerStyles(
                                  hideCalculationLabel
                                )}
                              >
                                {calculationResult}
                              </Box>
                            </Inline>
                          </Inline>
                        </Pressable>
                      </Box>
                    )}
                  </Tooltip>
                </Box>
              </Flex>
            );
          }}
        />
      }
    </Flex>
  );
};

export default QuickCalculationsSelect;
